import { useLanguage } from "@/contexts/LanguageContext";
import { useEffect, useRef, useState } from "react";

const LOGO_URL = "https://d2xsxph8kpxj0f.cloudfront.net/98466279/N8L9TKRXjtLMpzosC4Ao5k/mizano-icon-exact-192_e336f133.png";
const MAX_SPLASH_DURATION = 1500;
const FADE_DURATION = 250;

interface SplashScreenProps {
  onFinished: () => void;
  maxDuration?: number;
}

export function SplashScreen({ onFinished, maxDuration = MAX_SPLASH_DURATION }: SplashScreenProps) {
  const { language } = useLanguage();
  const [isExiting, setIsExiting] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const finishedRef = useRef(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setReducedMotion(query.matches);
    updatePreference();
    query.addEventListener("change", updatePreference);
    return () => query.removeEventListener("change", updatePreference);
  }, []);

  useEffect(() => {
    const nativeSplash = document.getElementById("native-splash");
    if (nativeSplash) {
      nativeSplash.style.opacity = "0";
      const removeNativeSplash = window.setTimeout(() => nativeSplash.remove(), FADE_DURATION);
      return () => window.clearTimeout(removeNativeSplash);
    }
  }, []);

  useEffect(() => {
    const finish = () => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      if (reducedMotion) {
        onFinished();
        return;
      }
      setIsExiting(true);
      window.setTimeout(onFinished, FADE_DURATION);
    };

    // Dismiss as soon as the browser completes its load work; the timeout is a hard cap.
    if (document.readyState === "complete") {
      const readyFrame = window.requestAnimationFrame(finish);
      const safetyTimer = window.setTimeout(finish, maxDuration);
      return () => {
        window.cancelAnimationFrame(readyFrame);
        window.clearTimeout(safetyTimer);
      };
    }

    window.addEventListener("load", finish, { once: true });
    const safetyTimer = window.setTimeout(finish, maxDuration);
    return () => {
      window.removeEventListener("load", finish);
      window.clearTimeout(safetyTimer);
    };
  }, [maxDuration, onFinished, reducedMotion]);

  return (
    <div className={`fixed inset-0 z-[9999] flex min-h-[100dvh] items-center justify-center overflow-hidden bg-[#0A0E16] px-6 safe-area-top safe-area-bottom ${reducedMotion ? "" : "transition-opacity duration-[250ms]"} ${isExiting ? "opacity-0" : "opacity-100"}`} aria-label="Loading Mizano" role="status">
      <div className="flex flex-col items-center text-center">
        <img src={LOGO_URL} alt="Mizano" className="h-[140px] w-[140px] rounded-[28px]" />
        <p className="mt-5 font-display text-[1.1rem] font-semibold tracking-[-.02em] text-[#F2F5F9]">Mizano</p>
        {language === "ar" && <p className="mt-1 text-sm text-[#69738A]">ميزانو</p>}
        <div className="mt-6 h-0.5 w-[120px] overflow-hidden rounded-full bg-[#212938]" aria-hidden="true">
          <div className={`h-full rounded-full bg-[#3B82F6] ${reducedMotion ? "w-1/2" : "animate-[splash-progress_1.5s_ease-out_forwards]"}`} />
        </div>
      </div>
    </div>
  );
}
